/**
 * ManualBroadcastTestSection — контрольная отправка себе из preview получателя.
 * Дата: 17.03.2026 - Использует ту же двухрежимную основу, что и broadcast queue.
 */
import { useState } from 'react';

import {
  manualBroadcastsApi,
  type SendManualBroadcastTestRequest,
  type SendManualBroadcastTestResponse,
} from '@/api/manualBroadcasts';
import { TestSelfSection, type TestSelfMode } from '@/components/features/delivery/TestSelfSection';
import { isDemoNotify } from '@/demo/isDemoNotify';

type ManualBroadcastTestMode = Exclude<TestSelfMode, 'channel_matrix'>;

function extractApiError(error: unknown, fallback: string): string {
  const maybeAxios = error as {
    response?: { data?: { error?: string; message?: string } };
    message?: string;
  };

  return (
    maybeAxios.response?.data?.error ||
    maybeAxios.response?.data?.message ||
    maybeAxios.message ||
    fallback
  );
}

interface ManualBroadcastTestSectionProps {
  request: Omit<SendManualBroadcastTestRequest, 'mode'>;
  tenantId?: string;
  currentChannel: string;
}

export function ManualBroadcastTestSection({
  request,
  tenantId,
  currentChannel,
}: ManualBroadcastTestSectionProps) {
  if (isDemoNotify()) return null;
  const [mode, setMode] = useState<ManualBroadcastTestMode>('snapshot');
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SendManualBroadcastTestResponse | null>(null);

  const handleSend = async () => {
    setIsSending(true);
    setError(null);
    setResult(null);

    try {
      const response = await manualBroadcastsApi.sendTestRecipient({ ...request, mode }, tenantId);
      setResult(response);
      if (!response.success) {
        setError(response.error || response.message || 'Не удалось отправить контрольную копию');
      }
    } catch (sendError) {
      setError(extractApiError(sendError, 'Не удалось отправить контрольную копию'));
    } finally {
      setIsSending(false);
    }
  };

  return (
    <TestSelfSection
      currentChannel={currentChannel}
      mode={mode}
      onModeChange={(nextMode) => {
        if (nextMode !== 'channel_matrix') {
          setMode(nextMode);
        }
      }}
      onSend={handleSend}
      allowChannelMatrix={false}
      selectedChannels={[]}
      onSelectedChannelsChange={() => {}}
      isSending={isSending}
      error={error}
      result={result}
    />
  );
}
